import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserPlus, RefreshCw, Trash2, Search, FileText, Eye, UserCheck, X, Check, Info, ChevronDown, Loader2 } from 'lucide-react';
import { API_URL } from '../config/api';
import { useFiliale } from '../context/FilialeContext';
import BlueHeader from '../components/BlueHeader';
import http from '../api/http';

const STATUTS = [
  { value: 'recue', label: 'Reçue', className: 'bg-slate-100 text-slate-700 ring-slate-500/20', bar: 'bg-slate-400' },
  { value: 'test', label: 'Test', className: 'bg-purple-50 text-purple-700 ring-purple-600/20', bar: 'bg-purple-500' },
  { value: 'en_examen', label: 'En examen', className: 'bg-amber-50 text-amber-800 ring-amber-600/20', bar: 'bg-amber-500' },
  { value: 'entretien', label: 'Entretien', className: 'bg-sky-50 text-sky-700 ring-sky-600/20', bar: 'bg-sky-500' },
  { value: 'offre', label: 'Offre', className: 'bg-indigo-50 text-indigo-700 ring-indigo-600/20', bar: 'bg-indigo-500' },
  { value: 'embauchee', label: 'Embauchée', className: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20', bar: 'bg-emerald-500' },
  { value: 'refusee', label: 'Refusée', className: 'bg-rose-50 text-rose-700 ring-rose-600/20', bar: 'bg-rose-500' },
  { value: 'retiree', label: 'Retirée', className: 'bg-gray-100 text-gray-600 ring-gray-500/20', bar: 'bg-gray-400' },
];

/* ════════════════════════════════════════════════════════════════════
   Éléments de présentation uniquement (aucune action métier ici)
   ════════════════════════════════════════════════════════════════════ */

const SEUIL_TRIAGE = 60;

const cx = (...c) => c.filter(Boolean).join(' ');

const initialsOf = (prenom, nom) => `${prenom?.[0] || ''}${nom?.[0] || ''}`.toUpperCase();

const controlCls =
  'w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 transition placeholder:text-slate-400 hover:border-slate-400 focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-600/20';

const btnPrimary =
  'inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600/40 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60';

const btnSecondary =
  'inline-flex items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600/40 disabled:cursor-not-allowed disabled:opacity-50';

const btnEmerald =
  'inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-emerald-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600/40 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60';

const rowIconBtn =
  'inline-flex rounded-md p-1.5 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600/40';

const Avatar = ({ prenom, nom, className = 'h-9 w-9 text-xs' }) => (
  <span className={cx('flex shrink-0 items-center justify-center rounded-full bg-blue-100 font-semibold text-blue-700', className)}>
    {initialsOf(prenom, nom) || '?'}
  </span>
);

const Field = ({ label, htmlFor, className = '', children }) => (
  <div className={className}>
    <label htmlFor={htmlFor} className="mb-1.5 block text-sm font-medium text-slate-700">{label}</label>
    {children}
  </div>
);

const SelectBox = ({ children, ...props }) => (
  <div className="relative">
    <select className={cx(controlCls, 'appearance-none pr-9')} {...props}>{children}</select>
    <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
  </div>
);

// Jauge compacte : le repère noir marque le seuil de réussite
const MiniGauge = ({ value }) => {
  const v = Math.max(0, Math.min(100, Number(value) || 0));
  return (
    <span className="relative block h-1.5 w-20 rounded-full bg-slate-100" aria-hidden="true">
      <span
        className={cx('absolute left-0 top-0 h-full rounded-full', v >= SEUIL_TRIAGE ? 'bg-emerald-500' : 'bg-rose-500')}
        style={{ width: `${v}%` }}
      />
      <span className="absolute -top-0.5 h-2.5 w-0.5 rounded bg-slate-700" style={{ left: `${SEUIL_TRIAGE}%` }} />
    </span>
  );
};

const Candidatures = () => {
  const { filialeId } = useFiliale();
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [offres, setOffres] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ statut: '', offre_id: '', q: '' });
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState('');
  const [tempStatut, setTempStatut] = useState('');
  const [onboardingData, setOnboardingData] = useState({
    date_embauche: '',
    type_contrat: 'CDI',
    commentaire: ''
  });
  const [showTriage, setShowTriage] = useState(false);
  const [triageSelection, setTriageSelection] = useState([]);
  const [triageRunning, setTriageRunning] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filialeId && filialeId !== 'all') params.filiale_id = filialeId;
      if (filters.statut) params.statut = filters.statut;
      if (filters.offre_id) params.offre_id = filters.offre_id;
      if (filters.q) params.q = filters.q;
      const response = await http.get('/api/candidatures', { params });
      setData(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error(error);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchOffres = async () => {
    try {
      const params = filialeId && filialeId !== 'all' ? { filiale_id: filialeId } : {};
      const res = await http.get('/api/offres', { params });
      setOffres(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    fetchData();
    fetchOffres();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filialeId]);

  const changeStatut = async (id, statut) => {
    try {
      const payload = { statut, notes_rh: notes || undefined };

      // Si embauchée, ajouter les données d'onboarding
      if (statut === 'embauchee' && onboardingData.date_embauche && onboardingData.type_contrat) {
        payload.date_embauche = onboardingData.date_embauche;
        payload.type_contrat = onboardingData.type_contrat;
        payload.commentaire = onboardingData.commentaire;
      }

      const response = await http.patch(`/api/candidatures/${id}/statut`, payload);

      setSelected(null);
      setNotes('');
      setTempStatut('');
      setOnboardingData({ date_embauche: '', type_contrat: 'CDI', commentaire: '' });
      fetchData();

      if (response.data.onboarding) {
        alert(`Candidature transformée en employé ! ID employé: ${response.data.onboarding.personnelId}`);
      }
    } catch (error) {
      alert(error.response?.data?.error || 'Erreur statut');
    }
  };

  // Candidatures éligibles au triage : statut Test et score suffisant
  const eligibleTriage = data.filter(c => c.statut === 'test' && c.score_test !== null && c.score_test >= 60);

  // Triage automatique : ouvre un aperçu pour valider la liste avant d'agir
  const handleTriageAuto = () => {
    const candidatesToTriage = data.filter(c => c.statut === 'test' && c.score_test !== null && c.score_test >= 60);
    if (candidatesToTriage.length === 0) {
      alert('Aucune candidature éligible pour le triage (statut = Test, score ≥ 60%)');
      return;
    }
    setTriageSelection(candidatesToTriage.map(c => c.id));
    setShowTriage(true);
  };

  const runTriageAuto = async () => {
    const candidatesToTriage = eligibleTriage.filter(c => triageSelection.includes(c.id));
    if (candidatesToTriage.length === 0) return;
    setTriageRunning(true);
    try {
      let successCount = 0;
      for (const candidate of candidatesToTriage) {
        try {
          await http.patch(`/api/candidatures/${candidate.id}/statut`, { statut: 'en_examen' });
          successCount++;
        } catch (error) {
          console.error(`Erreur triage candidature ${candidate.id}:`, error);
        }
      }

      setShowTriage(false);
      fetchData();
      alert(`${successCount}/${candidatesToTriage.length} candidatures passées en examen`);
    } catch (error) {
      alert('Erreur lors du triage automatique');
    } finally {
      setTriageRunning(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette candidature ?')) return;
    try {
      await http.delete(`/api/candidatures/${id}`);
      fetchData();
    } catch (error) {
      alert(error.response?.data?.error || 'Erreur suppression');
    }
  };

  const badge = (statut) => {
    const s = STATUTS.find((x) => x.value === statut) || STATUTS[0];
    return <span className={cx('inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset', s.className)}>{s.label}</span>;
  };

  /* ───── Données d'affichage ───── */

  const counts = STATUTS
    .map((s) => ({ ...s, count: data.filter((d) => d.statut === s.value).length }))
    .filter((s) => s.count > 0);
  const countsTotal = counts.reduce((n, s) => n + s.count, 0);

  return (
    <div className="min-h-screen bg-slate-50">
      <BlueHeader
        title="Candidatures"
        subtitle="Pipeline de recrutement"
        icon={UserPlus}
        actions={[
          { label: 'Actualiser', icon: RefreshCw, onClick: fetchData, primary: false },
          { label: 'Triage auto', icon: UserCheck, onClick: handleTriageAuto, primary: true }
        ]}
      />

      <div className="w-full space-y-6 px-4 py-6 sm:px-6 lg:px-8">
        {/* Vue d'ensemble */}
        {!loading && data.length > 0 && (
          <section className="rounded-2xl border border-slate-200 bg-white p-6">
            <div className="flex flex-wrap items-center justify-between gap-6">
              <div>
                <p className="text-sm text-slate-500">Candidatures affichées</p>
                <p className="text-3xl font-semibold tabular-nums tracking-tight text-slate-900">{data.length}</p>
              </div>

              {eligibleTriage.length > 0 && (
                <div className="flex flex-wrap items-center gap-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3">
                  <UserCheck className="h-5 w-5 shrink-0 text-emerald-600" />
                  <p className="text-sm text-emerald-900">
                    <span className="font-semibold">{eligibleTriage.length} candidature{eligibleTriage.length > 1 ? 's' : ''}</span>{' '}
                    {eligibleTriage.length > 1 ? 'ont' : 'a'} réussi le test (score d'au moins {SEUIL_TRIAGE}%) et {eligibleTriage.length > 1 ? 'peuvent' : 'peut'} passer en examen.
                  </p>
                  <button type="button" onClick={handleTriageAuto} className={btnEmerald}>
                    Lancer le triage
                  </button>
                </div>
              )}
            </div>

            <div className="mt-5 flex h-2.5 overflow-hidden rounded-full bg-slate-100" role="img" aria-label="Répartition des candidatures par statut">
              {counts.map((s) => (
                <div
                  key={s.value}
                  className={cx('h-full border-r-2 border-white last:border-r-0', s.bar)}
                  style={{ width: `${(s.count / countsTotal) * 100}%` }}
                  title={`${s.label} : ${s.count}`}
                />
              ))}
            </div>
            <ul className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm">
              {counts.map((s) => (
                <li key={s.value} className="flex items-center gap-2 text-slate-600">
                  <span className={cx('h-2.5 w-2.5 rounded-full', s.bar)} />
                  {s.label}
                  <span className="font-semibold tabular-nums text-slate-900">{s.count}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Filtres */}
        <div className="flex flex-wrap items-end gap-4 rounded-2xl border border-slate-200 bg-white p-4">
          <Field label="Recherche" htmlFor="filtre-recherche" className="min-w-[220px] flex-[2]">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                id="filtre-recherche"
                className={cx(controlCls, 'pl-9')}
                placeholder="Nom, e-mail, offre…"
                value={filters.q}
                onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                onKeyDown={(e) => e.key === 'Enter' && fetchData()}
              />
            </div>
          </Field>
          <Field label="Offre" htmlFor="filtre-offre" className="min-w-[180px] flex-1">
            <SelectBox id="filtre-offre" value={filters.offre_id} onChange={(e) => setFilters({ ...filters, offre_id: e.target.value })}>
              <option value="">Toutes</option>
              {offres.map((o) => <option key={o.id} value={o.id}>{o.titre}</option>)}
            </SelectBox>
          </Field>
          <Field label="Statut" htmlFor="filtre-statut" className="min-w-[160px] flex-1">
            <SelectBox id="filtre-statut" value={filters.statut} onChange={(e) => setFilters({ ...filters, statut: e.target.value })}>
              <option value="">Tous</option>
              {STATUTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </SelectBox>
          </Field>
          <button type="button" onClick={fetchData} className={btnPrimary}>Filtrer</button>
        </div>

        {/* Liste */}
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
          {loading ? (
            <div className="flex h-48 items-center justify-center text-sm text-slate-500">Chargement…</div>
          ) : data.length === 0 ? (
            <div className="px-4 py-16 text-center text-sm text-slate-500">Aucune candidature.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200">
                <thead className="bg-slate-50">
                  <tr>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Candidat</th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Offre</th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Source</th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Statut</th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Score Test</th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Date</th>
                    <th scope="col" className="px-4 py-3 text-right text-xs font-semibold text-slate-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {data.map((item) => (
                    <tr key={item.id} className="transition hover:bg-slate-50/70">
                      <td className="px-4 py-3.5">
                        <div className="flex items-center gap-3">
                          <Avatar prenom={item.prenom} nom={item.nom} />
                          <div className="min-w-0">
                            <div className="font-medium text-slate-900">{item.prenom} {item.nom}</div>
                            <div className="text-xs text-slate-500">{item.email}</div>
                            {item.telephone && <div className="text-xs text-slate-500">{item.telephone}</div>}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3.5 text-sm text-slate-700">{item.offre_titre}</td>
                      <td className="px-4 py-3.5 text-sm capitalize text-slate-700">{item.source || '—'}</td>
                      <td className="px-4 py-3.5">
                        {badge(item.statut)}
                        {item.statut === 'test' && item.score_test !== null && item.score_test >= SEUIL_TRIAGE && (
                          <div className="mt-1 text-xs font-medium text-emerald-700">Éligible au triage</div>
                        )}
                      </td>
                      <td className="px-4 py-3.5">
                        {item.score_test !== null ? (
                          <div className="flex items-center gap-3">
                            <span className={cx('w-12 font-semibold tabular-nums', item.score_test >= 60 ? 'text-emerald-600' : 'text-rose-600')}>
                              {item.score_test}%
                            </span>
                            <MiniGauge value={item.score_test} />
                          </div>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3.5 text-sm text-slate-600">
                        {item.created_at ? new Date(item.created_at).toLocaleDateString('fr-FR') : '—'}
                      </td>
                      <td className="px-4 py-3.5">
                        <div className="flex items-center justify-end gap-1">
                          {item.cv_path && (
                            <a
                              href={`${API_URL}${item.cv_path}`}
                              target="_blank"
                              rel="noreferrer"
                              className={cx(rowIconBtn, 'text-blue-600 hover:bg-blue-50')}
                              title="CV"
                              aria-label="Voir le CV"
                            >
                              <FileText className="h-4 w-4" />
                            </a>
                          )}
                          <button
                            type="button"
                            className={cx(rowIconBtn, 'text-blue-600 hover:bg-blue-50')}
                            title="Voir le profil"
                            aria-label="Voir le profil"
                            onClick={() => navigate(`/candidatures/${item.id}`)}
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            className="mx-1 rounded-md border border-slate-300 px-2.5 py-1 text-xs font-medium text-slate-700 transition hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600/40"
                            onClick={() => { setSelected(item); setNotes(item.notes_rh || ''); setTempStatut(''); }}
                          >
                            Pipeline
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(item.id)}
                            className={cx(rowIconBtn, 'text-rose-600 hover:bg-rose-50 focus-visible:ring-rose-600/40')}
                            title="Supprimer"
                            aria-label="Supprimer la candidature"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal Pipeline */}
      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-sm">
          <div
            role="dialog"
            aria-modal="true"
            aria-label={`${selected.prenom} ${selected.nom}`}
            className="flex max-h-[90vh] w-full max-w-xl flex-col rounded-2xl bg-white shadow-2xl ring-1 ring-black/10"
          >
            <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-6 py-4">
              <div className="flex min-w-0 items-center gap-3">
                <Avatar prenom={selected.prenom} nom={selected.nom} className="h-11 w-11 text-sm" />
                <div className="min-w-0">
                  <h3 className="truncate text-lg font-semibold text-slate-900">{selected.prenom} {selected.nom}</h3>
                  <p className="truncate text-sm text-slate-500">{selected.offre_titre}</p>
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                {badge(selected.statut)}
                <button
                  type="button"
                  onClick={() => setSelected(null)}
                  aria-label="Fermer"
                  className="rounded-lg p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600/40"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-6 py-5">
              {selected.lettre_motivation && (
                <div>
                  <p className="mb-1.5 text-sm font-medium text-slate-700">Lettre de motivation</p>
                  <div className="max-h-32 overflow-y-auto whitespace-pre-wrap rounded-lg bg-slate-50 p-3 text-sm leading-relaxed text-slate-700">
                    {selected.lettre_motivation}
                  </div>
                </div>
              )}

              <Field label="Notes RH" htmlFor="notes-rh">
                <textarea id="notes-rh" className={controlCls} rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
              </Field>

              <div>
                <p className="mb-2 text-sm font-medium text-slate-700">Changer le statut</p>
                <div role="radiogroup" aria-label="Nouveau statut" className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                  {STATUTS.map((s) => {
                    const isCurrent = selected.statut === s.value;
                    const isChosen = tempStatut === s.value;
                    return (
                      <button
                        key={s.value}
                        type="button"
                        role="radio"
                        aria-checked={isChosen}
                        onClick={() => setTempStatut(s.value)}
                        className={cx(
                          'relative rounded-lg border px-3 py-2 text-left text-sm transition focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600/40',
                          isChosen ? 'border-blue-600 bg-blue-50 ring-1 ring-blue-600' : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50',
                        )}
                      >
                        <span className="flex items-center gap-2 font-medium text-slate-900">
                          <span className={cx('h-2 w-2 shrink-0 rounded-full', s.bar)} />
                          {s.label}
                        </span>
                        {isCurrent && <span className="mt-0.5 block text-[11px] text-slate-500">Statut actuel</span>}
                        {isChosen && <Check className="absolute right-2 top-2 h-4 w-4 text-blue-600" />}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Champs d'onboarding pour "Embauchée" */}
              {tempStatut === 'embauchee' && (
                <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
                  <h4 className="mb-1 flex items-center gap-2 text-sm font-semibold text-blue-900">
                    <Info className="h-4 w-4" />
                    Onboarding automatique
                  </h4>
                  <p className="mb-4 text-xs text-blue-800">
                    Un employé sera automatiquement créé avec un contrat en brouillon.
                  </p>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <Field label="Date d'embauche *" htmlFor="onb-date">
                      <input
                        id="onb-date"
                        type="date"
                        className={controlCls}
                        value={onboardingData.date_embauche}
                        onChange={(e) => setOnboardingData({ ...onboardingData, date_embauche: e.target.value })}
                      />
                    </Field>
                    <Field label="Type de contrat *" htmlFor="onb-contrat">
                      <SelectBox
                        id="onb-contrat"
                        value={onboardingData.type_contrat}
                        onChange={(e) => setOnboardingData({ ...onboardingData, type_contrat: e.target.value })}
                      >
                        <option value="CDI">CDI</option>
                        <option value="CDD">CDD</option>
                        <option value="Stage">Stage</option>
                        <option value="Apprentissage">Apprentissage</option>
                      </SelectBox>
                    </Field>
                  </div>
                  <Field label="Commentaire (optionnel)" htmlFor="onb-commentaire" className="mt-3">
                    <textarea
                      id="onb-commentaire"
                      className={controlCls}
                      rows={2}
                      value={onboardingData.commentaire}
                      onChange={(e) => setOnboardingData({ ...onboardingData, commentaire: e.target.value })}
                      placeholder="Informations complémentaires sur l'embauche..."
                    />
                  </Field>
                </div>
              )}
            </div>

            <div className="flex items-center justify-between gap-3 border-t border-slate-200 px-6 py-4">
              <p className="text-xs text-slate-500">{tempStatut ? '' : 'Choisissez un statut pour valider.'}</p>
              <div className="flex gap-3">
                <button type="button" onClick={() => setSelected(null)} className={btnSecondary}>Annuler</button>
                <button
                  type="button"
                  onClick={() => changeStatut(selected.id, tempStatut)}
                  disabled={!tempStatut}
                  className={btnPrimary}
                >
                  Valider
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Triage automatique */}
      {showTriage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-sm">
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Triage automatique"
            className="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-2xl bg-white shadow-2xl ring-1 ring-black/10"
          >
            <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-6 py-4">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">Triage automatique</h3>
                <p className="mt-0.5 text-sm text-slate-500">
                  Candidatures au statut « Test » avec un score d'au moins {SEUIL_TRIAGE}%.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowTriage(false)}
                disabled={triageRunning}
                aria-label="Fermer"
                className="rounded-lg p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600/40 disabled:opacity-50"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
              <p className="text-sm leading-relaxed text-slate-600">
                Les candidatures cochées passeront au statut « En examen ». Décochez celles que vous préférez étudier à la main.
              </p>

              <div className="mb-2 mt-5 flex items-center justify-between">
                <p className="text-sm font-medium text-slate-900">
                  {triageSelection.length} sur {eligibleTriage.length} sélectionnée{triageSelection.length > 1 ? 's' : ''}
                </p>
                <button
                  type="button"
                  onClick={() => setTriageSelection(triageSelection.length === eligibleTriage.length ? [] : eligibleTriage.map(c => c.id))}
                  className="rounded text-sm font-medium text-blue-700 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600/40"
                >
                  {triageSelection.length === eligibleTriage.length ? 'Tout désélectionner' : 'Tout sélectionner'}
                </button>
              </div>

              <ul className="divide-y divide-slate-100 overflow-hidden rounded-xl border border-slate-200">
                {eligibleTriage.map((c) => {
                  const checked = triageSelection.includes(c.id);
                  return (
                    <li key={c.id}>
                      <label className={cx('flex cursor-pointer items-center gap-3 px-4 py-3 transition', checked ? 'bg-emerald-50/40' : 'hover:bg-slate-50')}>
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={triageRunning}
                          onChange={() => setTriageSelection(checked ? triageSelection.filter(x => x !== c.id) : [...triageSelection, c.id])}
                          className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-600"
                        />
                        <Avatar prenom={c.prenom} nom={c.nom} />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-medium text-slate-900">{c.prenom} {c.nom}</span>
                          <span className="block truncate text-xs text-slate-500">{c.offre_titre}</span>
                        </span>
                        <span className="flex items-center gap-3">
                          <MiniGauge value={c.score_test} />
                          <span className="w-12 text-right text-sm font-semibold tabular-nums text-emerald-600">{c.score_test}%</span>
                        </span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            </div>

            <div className="flex justify-end gap-3 border-t border-slate-200 px-6 py-4">
              <button type="button" onClick={() => setShowTriage(false)} disabled={triageRunning} className={btnSecondary}>
                Annuler
              </button>
              <button
                type="button"
                onClick={runTriageAuto}
                disabled={triageRunning || triageSelection.length === 0}
                className={btnEmerald}
              >
                {triageRunning ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" />
                    Traitement en cours…
                  </>
                ) : (
                  <>
                    <UserCheck className="h-4 w-4" />
                    Passer {triageSelection.length} candidature{triageSelection.length > 1 ? 's' : ''} en examen
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Candidatures;